import { Component, OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import{ SharedService } from '../../../services/shared.service';
import { PropertyService } from '../../../../modules/intake/services/property/property.service';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import Swal from 'sweetalert2';
@Component({
  selector: 'app-online-scheduling-confirm',
  templateUrl: './online-scheduling-confirm.component.html',
  styleUrls: ['./online-scheduling-confirm.component.scss']
})
export class OnlineSchedulingConfirmComponent implements OnInit {
  @Input() inspectionData: any;
  @Input() inspectors: any;
  form: FormGroup;
  jobCreatedId= "";
  generalFields = [
    'inspection_type',
    'address',
    'service_date',
    'arrival_time',
    'duration',
    'base_charge',
    'discount',
    'amount_paid',
    'foundation_type',
    'property_type',
    'property_sqft',
    'year_built',
    'multistory',
    'lock_box_code'
  ];

  contactSections = ['owner', 'buyer', 'buyer_agent', 'listing_agent', 'scheduler', 'payer'];

  // branch lookup by property zip, same as Intake property info
  branches: any = [];
  branchDetails = { name: '', zip: '', city: '', county: '', msg: '' };
  branch_display = false;
  // set when a branch covers every CA zip - the branch is then assigned silently
  all_ca_zips = false;

  constructor(
    public activeModal: NgbActiveModal,
    private modelService: NgbModal,
    private fb: FormBuilder,
    private sharedService:SharedService,
    private propertyService: PropertyService,
  ) {}

  ngOnInit() {

    this.createForm();
    this.getBranches();
    this.watchAddress();

    if (this.inspectionData) {
      this.prefillForm(this.inspectionData.body_json);
    }
  }

  	formatField(value: string): string {
	  return value.replace(/_/g, ' ');
	}

  createForm() {
    const generalControls = this.generalFields.reduce((acc, field) => {
	    // Add validation for specific required fields
	    if (['address', 'property_sqft', 'year_built'].indexOf(field) >= 0) {
	      acc[field] = ['', Validators.required];
	    } else {
	      acc[field] = [''];
	    }
	    return acc;
	  }, {} as any);

    this.form = this.fb.group({
    	inspector_id: ['', Validators.required], // Add this
    	access_details : [''],
    	branch_id: ['', Validators.required],
      ...generalControls,
      role_mapping: this.fb.group({
        owner: [''],
        buyer: [''],
        buyer_agent: [''],
        listing_agent: [''],
        scheduler: [''],
        payer: [''],
      }),
    });
  }

  	prefillForm(data: any) {
	  if (data.general) {
	    const inspectorName = data.general.inspector_name;

	    if (inspectorName && this.inspectors && this.inspectors.length) {
	      const match = this.inspectors.find(user =>
	        `${user.user_first_name} ${user.user_last_name}`.toLowerCase() === inspectorName.toLowerCase()
	      );
	      if (match) {
	        this.form.get('inspector_id').setValue(match.user_id);
	      }
	    }

	    // Fill rest of the fields (excluding inspector_name)
	    const rest = { ...data.general };
	    delete rest.inspector_name;

	    this.form.patchValue(rest);
	  }

	  const roles = this.form.get('role_mapping') as FormGroup;
	  const candidates = ['owner', 'buyer', 'buyer_agent', 'listing_agent', 'scheduler', 'payer'];

	  for (const role of candidates) {
	    const contact = data[role];
	    if (contact && (contact.name || contact.email || contact.phone)) {
	      if (roles.get(role)) {
	        roles.get(role).setValue(role);
	      }
	    }
	  }
	}

	getAvailableContactSections(): string[] {
	  const available = [];

	  if (!this.inspectionData['body_json']) return [];

	  const sections = ['owner', 'buyer', 'buyer_agent', 'listing_agent', 'scheduler', 'payer'];

	  for (let section of sections) {
	    const contact = this.inspectionData['body_json'][section];
	    if (contact && (contact.name || contact.email || contact.phone || contact.company)) {
	      available.push(section);
	    }
	  }

	  return available;
	}

	getContactLabel(section: string): string {
	  if (!this.inspectionData || !this.inspectionData['body_json'][section]) {
	    return section.replace('_', ' ').toUpperCase();
	  }

	  const contact = this.inspectionData['body_json'][section];
	  const parts = [];

	  if (contact.name) {
	    parts.push(contact.name);
	  }
	  if (contact.email) {
	    parts.push(contact.email);
	  }
	  if (contact.phone) {
	    parts.push(contact.phone);
	  }
	  if (contact.company) {
	    parts.push(contact.company);
	  }

	  return parts.length > 0 ? parts.join(' | ') : section.replace('_', ' ').toUpperCase();
	}
	getBranches() {
	  this.sharedService.getBranch().subscribe(
	    data => {
	      this.branches = data;
	    }
	  );
	}

	// re-run the branch lookup whenever the zip at the end of the address changes
	watchAddress() {
	  this.form.get('address').valueChanges.pipe(
	    debounceTime(400),
	    map(address => this.getZip(address)),
	    distinctUntilChanged()
	  ).subscribe(zip => this.checkZip(zip));
	}

	getZip(address: string): string {
	  const match = (address || '').toString().match(/(\d{5})(?:-\d{4})?\s*$/);
	  return match ? match[1] : '';
	}

	checkZip(zip: string) {
	  this.branch_display = false;
	  this.branchDetails.msg = '';
	  if (!zip) {
	    return;
	  }
	  this.propertyService.checkZip(zip).subscribe(
	    data => {
	      this.all_ca_zips = !!data.all_ca_zips;
	      if (data.status == 200) {
	        this.branchDetails.name = data.branch_name;
	        this.branchDetails.zip = data.zipcode_zip;
	        this.branchDetails.city = data.zipcode_city;
	        this.branchDetails.county = data.zipcode_county;
	        this.branch_display = !this.all_ca_zips;
	        this.form.get('branch_id').setValue(String(data.id));
	      } else {
	        this.form.get('branch_id').setValue('');
	      }
	      this.branchDetails.msg = data.msg;
	    },
	    error => {
	      console.error('Branch lookup failed, error:', error);
	    }
	  );
	}

	// manual override - hide the lookup result, as Intake does
	changeBranch() {
	  this.branch_display = false;
	  this.branchDetails.msg = '';
	}

	getUserFullName(user: any): string {
	  return `${user.user_first_name} ${user.user_last_name}`.trim();
	}


	markFormGroupTouched(formGroup: FormGroup) {
	  Object.keys(formGroup.controls).forEach(key => {
	    const control = formGroup.get(key);
	    if (control instanceof FormGroup) {
	      this.markFormGroupTouched(control);
	    } else {
	      control.markAsTouched();
	    }
	  });
	}
  onSubmit() {
  	if (this.form.invalid) {
	    this.markFormGroupTouched(this.form); // show validation messages
	    return;
	  }
    const { role_mapping, ...general } = this.form.value;
    const result = {
      general,
      roles: role_mapping,
      id : this.inspectionData.id
    };
    this.sharedService.createOnlineBookingJob(result).subscribe(
	  	data => { 
	 		console.log(data);
	 		this.jobCreatedId = data.data;
	 		Swal({
                title: 'Success',
                text: 'Added successfully'
            })
		}
	);
  }
}
